import { reactive } from 'vue'
import { Feature, PolygonGeometry, PointGeometry } from '@/entities/Feature'
import { AvionLocationSpace, Device } from '@/entities/AvionLocation'
import { Floorplan } from '@/entities/Floorplan'
import { createLogger } from '@/utils/logger'
import { fuzzyFilter } from '@/modules/filter'

const logger = createLogger('device-control-layer:store')

export type AvionDeviceStateName = 'dim' | 'on_off' | 'white' | 'clock'

export type AvionDeviceState = {
  humanized: string
  name: AvionDeviceStateName
  value: string
  read: boolean
  write: boolean
  configuration: {
    min_cct?: number
    max_cct?: number
  }
  operable: string
  updated_at: string
}

export type LayerControlDevice = Device & {
  statuses: AvionDeviceState[]
}

export type LayerControlSpace = AvionLocationSpace & {
  devices: LayerControlDevice[]
}

export interface AnimatedFeature {
  featureId: string
  element: HTMLElement
  lastMotionAt: string
  groupId?: string
  spaceId?: string
  sourceFeatureId?: string
}
type FeaturesToAnimate = Map<string, AnimatedFeature>

export interface LockdownEvent {
  type: 'device' | 'space' | 'group'
  pid: string
  lastMotionAt: string
}
export interface Incident {
  id: string
  ongoing: boolean
  startTime: string
  events: LockdownEvent[]
}

let state = reactive<{
  floorplans: Floorplan[]
  activeFloorplan: Floorplan | null
  filters: {
    searchValue: string
  }
  spaces: LayerControlSpace[]
  selectedFeature: {
    feature: Feature<PolygonGeometry | PointGeometry>
    type: 'space' | 'device' | 'group'
  }
  featuresToAnimate: FeaturesToAnimate
  noMotionSpaces: LayerControlSpace[]
  isAnimationRunning: boolean
  incident: Incident
}>({
  floorplans: [],
  activeFloorplan: null,
  filters: {
    searchValue: null,
  },
  spaces: [],
  selectedFeature: null,
  featuresToAnimate: new Map(),
  isAnimationRunning: false,
  noMotionSpaces: [],
  incident: {
    id: '',
    ongoing: false,
    startTime: '',
    events: [],
  },
})

export const createStore = () => {
  return {
    get floorplans() {
      return state.floorplans
    },
    get activeFloorplan() {
      return state.activeFloorplan
    },
    get features() {
      return state.activeFloorplan.data.features
    },
    get spaces() {
      const floorplanSpaces = state.spaces.filter((space) => {
        return state.activeFloorplan.data.spaces.includes(space.pid)
      })

      if (!state.filters.searchValue) return floorplanSpaces

      return fuzzyFilter({
        items: floorplanSpaces,
        key: 'name',
        query: state.filters.searchValue || '',
      })
    },
    get selectedFeature() {
      return state.selectedFeature
    },
    get featuresToAnimate() {
      return state.featuresToAnimate
    },
    get isAnimationRunning() {
      return state.isAnimationRunning
    },
    get noMotionSpaces() {
      return state.noMotionSpaces
    },
    get incident() {
      return state.incident
    },
    setIncident(incident: Incident) {
      state.incident = incident
    },
    updateIncidentEvents(event: LockdownEvent) {
      state.incident.events = [...state.incident.events, event]
    },
    setNoMotionSpaces(rooms: LayerControlSpace[]) {
      state.noMotionSpaces = rooms
    },
    setAnimationRunning(isRunning: boolean) {
      state.isAnimationRunning = isRunning
    },
    getFeatureToAnimate(featureId: string) {
      return state.featuresToAnimate.get(featureId)
    },
    updateFeaturesToAnimate(featureId: string, newFeature: AnimatedFeature) {
      state.featuresToAnimate.set(featureId, newFeature)
    },
    removeFeatureToAnimate(featureId: string) {
      state.featuresToAnimate.delete(featureId)
    },
    clearFeaturesToAnimate() {
      state.featuresToAnimate.clear()
    },
    getSelectedSpace() {
      if (!state.selectedFeature) return

      const { feature, type } = state.selectedFeature
      const featureId = feature.id

      // Helper to find space by condition
      const findSpace = (condition: (space: any) => boolean) => state.spaces.find(condition)

      let space = null
      switch (type) {
        case 'space':
          space = findSpace((space) => `space:${space.pid}` === featureId)
          break
        case 'group':
          space = findSpace((space) => space.groups.some((group) => `group:${group.pid}` === featureId))
          break
        case 'device':
          space = findSpace((space) => space.devices.some((device) => `device:${device.pid}` === featureId))
          break
      }
      logger.debug('Resolved selected space', space, state.selectedFeature)
      return space
    },
    getSelectedGroup() {
      if (!state.selectedFeature) return null

      const { feature, type } = state.selectedFeature
      const featureId = feature.id

      let result = null

      switch (type) {
        case 'group': {
          state.spaces.some((space) => {
            result = space.groups.find((group) => `group:${group.pid}` === featureId) || null
            return result !== null
          })
          break
        }
        case 'device': {
          state.spaces.some((space) => {
            result =
              space.groups.find((group) => group.devices.some((device) => `device:${device}` === featureId)) || null
            return result !== null
          })
          break
        }
      }
      logger.debug('Resolved selected group', result, state.selectedFeature)
      return result
    },
    getLinkedGroups(space: AvionLocationSpace) {
      return space.groups.filter((group) => {
        return state.activeFloorplan.data.groups.includes(group.pid)
      })
    },
    getLinkedDevices(space: AvionLocationSpace) {
      return space.devices.filter((device) => {
        return state.activeFloorplan.data.devices.includes(device.pid)
      })
    },
    setFloorplans(floorplans: Floorplan[]) {
      logger.debug('Setting floorplans', floorplans)
      state.floorplans = floorplans
    },
    setFeatures(
      partialFeatures: Feature<PolygonGeometry | PointGeometry>[],
      partialDeviceStatuses: AvionDeviceState[],
    ) {
      state.activeFloorplan.data.features = state.activeFloorplan.data.features.map((feature) => {
        const change = partialFeatures.find((change) => change.id === feature.id)

        // merge properties
        if (change) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              ...change.properties,
            },
          }
        }

        // return feature as is if partial update does not include it
        return feature
      })

      // Update device statuses in each space
      //@ts-ignore
      state.spaces = state.spaces.map((space) => ({
        ...space,
        //@ts-ignore
        devices: space.devices.map((device) => {
          const deviceStatuses = partialDeviceStatuses[device.pid]

          if (!deviceStatuses) return device

          //@ts-ignore
          const updatedStatuses = device.statuses
            ? //@ts-ignore
              device.statuses.map((status: any) => {
                const match = deviceStatuses.find((ds: any) => ds.name === status.name)
                return match ? { ...status, value: match.value } : status
              })
            : deviceStatuses

          return { ...device, statuses: updatedStatuses }
        }),
      }))
    },
    setActiveFloorplan(floorplan: Floorplan) {
      state.activeFloorplan = floorplan
      state.spaces = floorplan.spaces
    },
    setSelectedFeature(feature?: Feature<PolygonGeometry | PointGeometry>, type?: 'space' | 'device' | 'group') {
      logger.debug(`Called setSelectedFeature with`, feature)
      if (!feature) {
        state.selectedFeature = null
        return
      }
      state.selectedFeature = { feature, type }
      logger.debug(`Selected feature`, state.selectedFeature)
    },
    hasFloorplans() {
      return state.floorplans.length > 0
    },
    isDeviceLinked(device: Device) {
      const linkedDevices = state.activeFloorplan.data.devices
      return linkedDevices.includes(device.pid)
    },
    setSearchValue(searchValue: string) {
      state.filters.searchValue = searchValue
      logger.debug(`Update search value`, state.filters.searchValue, typeof state.filters.searchValue)
    },
  }
}
export default createStore()
